import React, { useState, useEffect } from 'react';
import { ShieldCheck, Coins, Gift, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface SellerOnboardingModalProps {
  onAccept: () => void;
  isSubmitting?: boolean;
  creditsAmount?: number;
  boostAmount?: number;
  planLabel?: string;
  validityMonths?: number;
}

export function SellerOnboardingModal({ 
  onAccept, 
  isSubmitting = false,
  creditsAmount = 0,
  boostAmount = 0,
  planLabel = '',
  validityMonths = 12,
}: SellerOnboardingModalProps) {
  const [countdown, setCountdown] = useState(60);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const canAccept = countdown === 0 && accepted;
  const timerComplete = countdown === 0;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-background overflow-auto py-8"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <div className="flex flex-col items-center text-center p-6 md:p-8 max-w-2xl mx-auto">
        {/* Header */}
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
          <ShieldCheck className="w-10 h-10 text-green-500" />
        </div>
        
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
          🎉 Parabéns! Você é um Vendedor Verificado!
        </h1>
        
        <p className="text-muted-foreground text-lg mb-8">
          Bem-vindo ao Marketplace da Forja! Antes de começar a vender, é importante entender como funcionam as negociações.
        </p>

        {/* Content Sections */}
        <div className="w-full space-y-6 text-left">
          {/* Section 1: How Credits Work */}
          <div className="bg-muted/30 rounded-xl p-5 border border-border">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  Como Funcionam os Créditos de Negociação
                </h2>
                <p className="text-muted-foreground mb-3">
                  O Marketplace opera com <strong className="text-foreground">Créditos de Negociação</strong>. Cada vez que você conclui uma venda com sucesso, um crédito é consumido.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Os créditos só são consumidos quando <strong className="text-foreground">AMBAS as partes</strong> confirmam a negociação como concluída</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Negociações canceladas ou expiradas <strong className="text-foreground">NÃO consomem</strong> créditos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Os créditos têm validade até o final da sua verificação ({validityMonths} meses)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 2: Your Plan Credits */}
          <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/30">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  🎁 Seus Créditos do Plano
                </h2>
                <p className="text-muted-foreground mb-2">
                  Por ter adquirido o plano <strong className="text-foreground">{planLabel}</strong>, você ganhou <strong className="text-green-500 text-lg">{creditsAmount} {creditsAmount === 1 ? 'CRÉDITO' : 'CRÉDITOS'}</strong>!
                </p>
                <p className="text-sm text-muted-foreground">
                  Isso significa que você pode concluir <strong className="text-foreground">{creditsAmount} {creditsAmount === 1 ? 'negociação' : 'negociações'}</strong> no Marketplace. Após esgotar seus créditos, você precisará adquirir mais créditos para continuar vendendo.
                </p>
                {boostAmount > 0 && (
                  <p className="text-sm text-purple-400 mt-2">
                    Além disso, você recebeu <strong className="text-purple-500">+{boostAmount} boost</strong> de visibilidade no ranking!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Important Notice */}
          <div className="bg-yellow-500/10 rounded-xl p-5 border border-yellow-500/30">
            <p className="text-sm text-muted-foreground">
              <strong className="text-yellow-500">⚠️ Importante:</strong> A validade dos seus créditos é de {validityMonths} meses a partir da data de concessão. Após o vencimento, você precisará adquirir um novo plano para continuar utilizando o Marketplace.
            </p>
          </div>

          {/* Section 4: Rules and Monitoring */}
          <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30">
            <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              🔒 Regras de Uso e Monitoramento
            </h2>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Todas as negociações devem acontecer <strong className="text-foreground">exclusivamente pelo chat interno</strong> do Forja Hub.
              </p>
              <p>
                As conversas realizadas dentro da plataforma são <strong className="text-foreground">monitoradas e passíveis de auditoria</strong> pela equipe da Forja para garantir a segurança das negociações.
              </p>
              <p className="font-medium text-foreground">Qualquer tentativa de:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>negociar por fora da plataforma</li>
                <li>direcionar o comprador para contatos externos</li>
                <li>burlar o uso de créditos ou agir de má-fé</li>
              </ul>
              <p className="text-red-400 font-medium">
                resultará em banimento permanente, perda total dos créditos, cancelamento do acesso à comunidade A Forja e sem direito a reembolso.
              </p>
              <p className="pt-2 border-t border-red-500/20 mt-3">
                Ao continuar, você concorda integralmente com essas regras.
              </p>
            </div>
          </div>
        </div>

        {/* Timer and Acceptance */}
        <div className="w-full mt-8 pt-6 border-t border-border">
          {/* Timer */}
          <div className={cn(
            "flex items-center justify-center gap-2 mb-6 transition-all",
            timerComplete ? "text-green-500" : "text-muted-foreground"
          )}>
            <Clock className="w-5 h-5" />
            {timerComplete ? (
              <span className="text-sm font-medium">Você pode continuar agora</span>
            ) : (
              <span className="text-sm">
                Aguarde <strong className="text-foreground font-mono text-base">{countdown}</strong> segundos para continuar...
              </span>
            )}
          </div>

          {/* Checkbox */}
          <div className={cn(
            "flex items-start gap-3 p-4 rounded-lg border transition-all mb-6",
            timerComplete 
              ? "bg-muted/50 border-border cursor-pointer hover:bg-muted/70" 
              : "bg-muted/20 border-border/50 opacity-60 cursor-not-allowed"
          )}>
            <Checkbox
              id="accept-terms"
              checked={accepted}
              onCheckedChange={(checked) => timerComplete && setAccepted(checked === true)}
              disabled={!timerComplete}
              className="mt-0.5"
            />
            <label 
              htmlFor="accept-terms" 
              className={cn(
                "text-sm text-left",
                timerComplete ? "cursor-pointer text-foreground" : "cursor-not-allowed text-muted-foreground"
              )}
            >
              Declaro que compreendi as regras do Marketplace, o uso obrigatório do chat interno e as penalidades aplicáveis em caso de descumprimento.
            </label>
          </div>

          {/* Accept Button */}
          <Button
            onClick={onAccept}
            disabled={!canAccept || isSubmitting}
            size="lg"
            className="w-full md:w-auto md:px-12"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin mr-2" />
                Salvando...
              </>
            ) : (
              'Aceitar e Continuar'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
